import {
  CrossPointMarker,
  EllipsePointMarker,
  FastLineRenderableSeries,
  SplineLineRenderableSeries,
  SquarePointMarker,
  TrianglePointMarker,
  XyDataSeries,
  XyScatterRenderableSeries,
  type TSciChart,
  type IPointMarker
} from 'scichart'
import type { WaveCurve } from '../../../../interfaces/waveChart'

export type LineStyle = 'solid' | 'dashed' | 'dotted' | 'dashdot'
export type PointStyle = 'none' | 'circle' | 'square' | 'triangle' | 'cross'
export type InterpolationMethod = 'linear' | 'stepBefore' | 'stepAfter' | 'stepMiddle' | 'spline' | 'none'

export interface SeriesStyleOptions {
  lineStyle: LineStyle
  pointStyle: PointStyle
  interpolation: InterpolationMethod
  color: string
  thickness: number
  pointSize: number
  opacity: number
  showDataPoints: boolean
}

export interface SeriesCreationOptions {
  wasmContext: TSciChart
  dataSeries: XyDataSeries
  yAxisId: string
  style: SeriesStyleOptions
}

export interface SeriesValidationResult {
  isValid: boolean
  errors: string[]
}

/**
 * Factory class for creating and configuring SciChart series
 * Extracts all series creation logic from WaveChartCanvas for better maintainability
 */
export class ChartSeriesFactory {
  /**
   * Maps line style strings to SciChart stroke dash arrays
   */
  static getStrokeDashArray(lineStyle: LineStyle): number[] | undefined {
    switch (lineStyle) {
      case 'solid':
        return undefined // No dash array for solid lines
      case 'dashed':
        return [10, 5]
      case 'dotted':
        return [2, 2]
      case 'dashdot':
        return [10, 5, 2, 5]
      default:
        return undefined
    }
  }

  /**
   * Creates a point marker based on style configuration
   */
  static createPointMarker(
    wasmContext: TSciChart,
    pointStyle: PointStyle,
    size: number,
    color: string
  ): IPointMarker | undefined {
    if (pointStyle === 'none') {
      return undefined
    }

    const markerOptions = {
      width: size,
      height: size,
      fill: color,
      stroke: color,
      strokeThickness: 1
    }

    switch (pointStyle) {
      case 'circle':
        return new EllipsePointMarker(wasmContext, markerOptions)
      case 'square':
        return new SquarePointMarker(wasmContext, markerOptions)
      case 'triangle':
        return new TrianglePointMarker(wasmContext, markerOptions)
      case 'cross':
        return new CrossPointMarker(wasmContext, markerOptions)
      default:
        return undefined
    }
  }

  /**
   * Creates step interpolated data series
   */
  private static createStepDataSeries(
    wasmContext: TSciChart,
    originalData: XyDataSeries,
    stepType: 'stepBefore' | 'stepAfter' | 'stepMiddle'
  ): XyDataSeries {
    const stepDataSeries = new XyDataSeries(wasmContext, {
      dataSeriesName: `${originalData.dataSeriesName}_step`
    })

    // Get the original data points
    const xValues = originalData.getNativeXValues()
    const yValues = originalData.getNativeYValues()

    if (xValues.size() < 2) {
      // Not enough points for step interpolation
      return originalData
    }

    // Create step data points
    for (let i = 0; i < xValues.size(); i++) {
      const x = xValues.get(i)
      const y = yValues.get(i)

      if (i === 0) {
        // First point - always add as-is
        stepDataSeries.append(x, y)
      } else {
        const prevX = xValues.get(i - 1)
        const prevY = yValues.get(i - 1)

        switch (stepType) {
          case 'stepBefore':
            // Horizontal first, then vertical
            stepDataSeries.append(x, prevY) // Horizontal to current x
            stepDataSeries.append(x, y)     // Vertical to current y
            break

          case 'stepAfter':
            // Vertical first, then horizontal
            stepDataSeries.append(prevX, y) // Vertical to current y
            stepDataSeries.append(x, y)     // Horizontal to current x
            break

          case 'stepMiddle':
            // Step in the middle
            const midX = prevX + (x - prevX) / 2
            stepDataSeries.append(midX, prevY) // Horizontal to middle
            stepDataSeries.append(midX, y)     // Vertical at middle
            stepDataSeries.append(x, y)       // Horizontal to current x
            break
        }
      }
    }

    return stepDataSeries
  }

  /**
   * Creates the appropriate series type based on interpolation method
   */
  static createSeries(options: SeriesCreationOptions): FastLineRenderableSeries | SplineLineRenderableSeries | XyScatterRenderableSeries {
    const { wasmContext, dataSeries, yAxisId, style } = options
    
    // Only create point marker if showDataPoints is enabled
    const pointMarker = style.showDataPoints 
      ? this.createPointMarker(wasmContext, style.pointStyle, style.pointSize, style.color)
      : undefined

    let finalDataSeries = dataSeries

    // Handle step interpolation by transforming the data
    if (style.interpolation === 'stepBefore' || style.interpolation === 'stepAfter' || style.interpolation === 'stepMiddle') {
      finalDataSeries = this.createStepDataSeries(wasmContext, dataSeries, style.interpolation)
    }

    const baseOptions = {
      dataSeries: finalDataSeries,
      yAxisId,
      stroke: style.color,
      strokeThickness: style.thickness,
      opacity: style.opacity,
      strokeDashArray: this.getStrokeDashArray(style.lineStyle),
      pointMarker
    }

    switch (style.interpolation) {
      case 'spline':
        return new SplineLineRenderableSeries(wasmContext, baseOptions)
      
      case 'stepBefore':
      case 'stepAfter':
      case 'stepMiddle':
        // Use FastLineRenderableSeries with step-transformed data
        return new FastLineRenderableSeries(wasmContext, baseOptions)
      
      case 'none':
        // Points only, no line - use scatter series
        return new XyScatterRenderableSeries(wasmContext, {
          dataSeries,
          yAxisId,
          pointMarker: this.createPointMarker(wasmContext, style.pointStyle || 'circle', style.pointSize, style.color)
        })
      
      case 'linear':
      default:
        // Linear interpolation using standard FastLineRenderableSeries
        return new FastLineRenderableSeries(wasmContext, baseOptions)
    }
  }

  /**
   * Converts WaveCurve to SeriesStyleOptions with safe property access
   */
  static curveToStyleOptions(curve: WaveCurve): SeriesStyleOptions {
    return {
      lineStyle: (curve.lineStyle as LineStyle) || 'solid',
      pointStyle: (curve.pointStyle as PointStyle) || 'circle',
      interpolation: (curve.interpolation as InterpolationMethod) || 'linear',
      color: curve.color || '#1976d2',
      thickness: curve.strokeThickness || 1, // Use strokeThickness from WaveCurve
      pointSize: curve.pointSize || 4,
      opacity: curve.opacity ?? 1,
      showDataPoints: curve.showDataPoints ?? false // Include showDataPoints setting
    }
  }

  /**
   * Creates a series from a WaveCurve configuration
   */
  static createSeriesFromCurve(
    wasmContext: TSciChart,
    dataSeries: XyDataSeries,
    yAxisId: string,
    curve: WaveCurve
  ): FastLineRenderableSeries | SplineLineRenderableSeries | XyScatterRenderableSeries {
    const style = this.curveToStyleOptions(curve)
    
    const series = this.createSeries({
      wasmContext,
      dataSeries,
      yAxisId,
      style
    })
    // Apply visibility flag from curve (hide without removing)
    ;(series as any).isVisible = curve.visible !== false
    return series
  }

  /**
   * Validates series creation parameters
   */
  static validateSeriesOptions(options: SeriesCreationOptions): SeriesValidationResult {
    const errors: string[] = []
    
    if (!options.wasmContext) {
      errors.push('wasmContext is required')
    }
    
    if (!options.dataSeries) {
      errors.push('dataSeries is required')
    }
    
    if (!options.yAxisId) {
      errors.push('yAxisId is required')
    }
    
    if (!options.style.color) {
      errors.push('color is required')
    }
    
    if (options.style.thickness <= 0) {
      errors.push('thickness must be greater than 0')
    }
    
    if (options.style.pointSize <= 0) {
      errors.push('pointSize must be greater than 0')
    }
    
    if (options.style.opacity < 0 || options.style.opacity > 1) {
      errors.push('opacity must be between 0 and 1')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Validates WaveCurve for series creation
   */
  static validateCurve(curve: WaveCurve): SeriesValidationResult {
    const errors: string[] = []
    
    if (!curve.xValues || curve.xValues.length === 0) {
      errors.push('xValues is required and must not be empty')
    }
    
    if (!curve.yValues || curve.yValues.length === 0) {
      errors.push('yValues is required and must not be empty')
    }
    
    if (curve.xValues && curve.yValues && curve.xValues.length !== curve.yValues.length) {
      errors.push('xValues and yValues must have the same length')
    }
    
    if (curve.strokeThickness && curve.strokeThickness <= 0) {
      errors.push('strokeThickness must be greater than 0')
    }
    
    if (curve.pointSize && curve.pointSize <= 0) {
      errors.push('pointSize must be greater than 0')
    }
    
    if (curve.opacity !== undefined && (curve.opacity < 0 || curve.opacity > 1)) {
      errors.push('opacity must be between 0 and 1')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Gets all supported line styles
   */
  static getSupportedLineStyles(): LineStyle[] {
    return ['solid', 'dashed', 'dotted', 'dashdot']
  }

  /**
   * Gets all supported point styles (only SciChart-supported markers)
   */
  static getSupportedPointStyles(): PointStyle[] {
    return ['none', 'circle', 'square', 'triangle', 'cross']
  }

  /**
   * Gets all supported interpolation methods
   */
  static getSupportedInterpolationMethods(): InterpolationMethod[] {
    return ['linear', 'stepBefore', 'stepAfter', 'stepMiddle', 'spline', 'none']
  }

  /**
   * Creates default style options
   */
  static createDefaultStyleOptions(): SeriesStyleOptions {
    return {
      lineStyle: 'solid',
      pointStyle: 'none',
      interpolation: 'linear',
      color: '#1976d2',
      thickness: 1,
      pointSize: 3,
      opacity: 1,
      showDataPoints: false
    }
  }
} 
